import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from 'app/store';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import 'assets/css/main.scss';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import { userManager } from 'app/authUserManager';
import { I18nextProvider } from 'react-i18next';
import './i18n';
import i18n from './i18n';
import { LoaderProvider } from 'features/loaders/loaderProvider';
import { AuthProvider } from 'react-oidc-context';
import { InputRenderersProvider } from 'factories/input-factory/registry';
import { InputRenderers, RowInputRenderers } from 'factories/input-factory/types';
import { socleRenderers, socleRowRenderers } from 'factories/input-factory/socleRenderers';
import { referentialRenderers, referentialRowRenderers } from 'features/referentials/form/referentialRenderers';
import { contractualizationRenderers } from 'features/subscription-process/contractualization-process/forms/contractualizationRenderers';

// The composition root assembles the field renderers: core + every feature.
// The factory never imports a feature, and the registry types are complete,
// so forgetting a feature here is a compile error.
const inputRenderers: InputRenderers = {
  ...socleRenderers,
  ...referentialRenderers,
  ...contractualizationRenderers,
};
const rowRenderers: RowInputRenderers = {
  ...socleRowRenderers,
  ...referentialRowRenderers,
};

// L'overlay de webpack-dev-server traite le warning navigateur bénin
// "ResizeObserver loop..." comme une erreur bloquante (faux positif connu,
// déclenché par les composants qui s'auto-mesurent). On l'ignore en dev.
if (process.env.NODE_ENV === 'development') {
  const resizeObserverMessages = [
    'ResizeObserver loop completed with undelivered notifications.',
    'ResizeObserver loop limit exceeded'
  ];
  window.addEventListener('error', event => {
    if (!resizeObserverMessages.some(msg => event.message?.includes(msg)))
      return;
    event.stopImmediatePropagation();
    // L'overlay a pu être affiché avant notre handler : on le retire.
    document.getElementById('webpack-dev-server-client-overlay')?.remove();
  });
}

const container = document.getElementById('root')!;
const root = createRoot(container);
const persistor = persistStore(store);

// Réglages OIDC : app/authUserManager.ts (UserManager partagé, requis par
// l'impersonation pour basculer la session hors du cycle React).
const oidcConfig = {
  userManager,
  // Nettoyer les paramètres d'URL après le callback
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

root.render(
  <AuthProvider {...oidcConfig}>
    <I18nextProvider i18n={i18n}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <BrowserRouter>
            <LoaderProvider>
              <InputRenderersProvider renderers={inputRenderers} rowRenderers={rowRenderers}>
                <App />
              </InputRenderersProvider>
              <ToastContainer
                position="top-right"
                autoClose={2000}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
              />
            </LoaderProvider>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </I18nextProvider>
  </AuthProvider>
);
